/**
 * ErrorBoundary is a React component that you wrap around other components
 * and it will catch any errors that are thrown within them.
 *
 * When an error is caught ErrorBoundary will fire the function handleError,
 * which is passed as a prop.
 *
 * You can nest ErrorBoundarys.
 */
import React from 'react';
import PropTypes from 'prop-types';
import IPropTypes from 'react-immutable-proptypes';

import Alert from '../dialogs';

const ERROR_DEDUPE_TIME_MS = 500;

class ErrorBoundary extends React.Component {
    static displayName = 'ErrorBoundary';

    constructor(props) {
        super(props);
        this.state = { hasError: false };
        this.lastWindowError = null;
    }

    componentDidMount() {
        // Add a window error listener, to catch completely unhandled errors
        this._windowErrorHandler = event => {
            const { error } = event;
            if (error) {
                // if the error is just a string, convert it to an Error object
                // so we can add swErrorCategory
                const errObj = this.convertToErrorObject(error);
                errObj.swErrorCategory = 'unhandled';

                // Only send the error if it is more that 0.5s since the last
                // report of the same error:
                const now = performance.now();
                if (
                    !this.lastWindowError ||
                    this.lastWindowError.message !== errObj.message ||
                    now - this.lastWindowError.timestamp > ERROR_DEDUPE_TIME_MS
                ) {
                    this.lastWindowError = {
                        message: errObj.message,
                        timestamp: now,
                    };
                    this.props.handleError(errObj, null, false);
                }
            }
        };

        window.addEventListener('error', this._windowErrorHandler);
    }

    componentWillUnmount() {
        if (this._windowErrorHandler) {
            window.removeEventListener('error', this._windowErrorHandler);
        }
    }

    componentDidCatch(error, info) {
        this.setState({ hasError: true });
        // if the error is just a string, convert it to an Error object
        // so we can add swErrorCategory
        const errObj = this.convertToErrorObject(error);
        errObj.swErrorCategory = 'error-boundary';
        this.props.handleError(errObj, info.componentStack);
    }

    convertToErrorObject(err) {
        return typeof err === 'string' ? new Error(err) : err;
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className={'screen'}>
                    <Alert alertSpec={this.props.alert} />
                </div>
            );
        }
        return this.props.children;
    }
}

/**
 * handleError - a function called when an error that was thrown (error, info)
 *                  error - a string description of the error
 *                  info  - a string component stack trace
 * alert       - an alert to show when an error occurs
 * children    - the element(s) to wrap
 */
ErrorBoundary.propTypes = {
    handleError: PropTypes.func,
    alert: IPropTypes.map,
    children: PropTypes.element.isRequired,
};

export default ErrorBoundary;
